import * as React from 'react'
import { gql, useQuery } from '@apollo/client'
import query from '@packages/graphql/query.graphql'
import * as domain from '@packages/domain'
import { constUndefined, E, flow, O, pipe, RA, RNEA, RR, t, tt, Rnd, IO, identity } from '@packages/prelude'
import { formatValidationErrors } from 'io-ts-reporters'
import { Decoder } from '@packages/graphql'
import { react } from './commons'
import { PostList } from './Posts'
import { CategoriesFilter } from './Filter'
import { LoadingSpinner } from './components/LoadingSpinner'

const POSTS_QUERY = gql`
  ${query}
`

interface FilterCategory extends domain.Category.Type {
  posts: ReadonlyArray<domain.CombinedPost.Type>
}

interface AppProps {
  filter: string
  hideBar: boolean
  currentPostId?: number
}

type PostFilter = O.Option<number>

const placeholder: domain.Placeholder.Type = {
  type: 'Placeholder',
  title: 'Keine Beiträge ...' as tt.NonEmptyString,
  description: 'Aktuell sind keine Beiträge dieser Rubrik verfügbar' as tt.NonEmptyString
}

function getFilteredPosts<A extends Exclude<domain.CombinedPost.Type, domain.Placeholder.Type>>(
  filter: PostFilter,
  posts: RR.ReadonlyRecord<string, A>
): RR.ReadonlyRecord<string, A> {
  return pipe(
    filter,
    O.map(postFilter =>
      pipe(
        posts,
        RR.reject(post => post.databaseId === postFilter)
      )
    ),
    O.getOrElse(() => posts)
  )
}

export const App: React.VFC<AppProps> = ({ filter, hideBar, currentPostId }) => {
  const [categories, setCategories] = React.useState<RR.ReadonlyRecord<string, FilterCategory>>({})
  const [activeFilter, setActiveFilter] = React.useState<string>('')
  const [postFilter, setPostFilter] = React.useState<PostFilter>(O.none)
  const [filteredPosts, setFilteredPosts] = React.useState<ReadonlyArray<domain.CombinedPost.Type>>([])
  const [ads, setAds] = React.useState<ReadonlyArray<domain.Ad.Type>>(RA.zero)
  const { loading, data } = useQuery(POSTS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network'
  })

  React.useEffect(() => pipe(currentPostId, t.number.decode, O.fromEither, setPostFilter), [currentPostId])

  React.useEffect(() => {
    pipe(
      data,
      O.fromNullable,
      O.map(
        flow(
          Decoder.decode,
          E.mapLeft(errors => formatValidationErrors(errors as any).join('\\n')),
          E.map(({ categories, events, ads, raffles, podcasts }) => {
            pipe(
              RR.singleton<FilterCategory>('dates', {
                id: 'dates' as tt.NonEmptyString,
                icon: O.of('fa-solid fa-calendar-days'),
                name: 'Termine' as tt.NonEmptyString,
                slug: 'dates' as tt.NonEmptyString,
                parentId: O.none,
                posts: pipe(getFilteredPosts(postFilter, events), domain.Events.toReadonlyNonEmptyArray)
              }),
              RR.merge(
                RR.singleton<FilterCategory>('raffles', {
                  id: 'raffles' as tt.NonEmptyString,
                  icon: O.of('fak fa-luck'),
                  name: 'Verlosungen' as tt.NonEmptyString,
                  slug: 'raffles' as tt.NonEmptyString,
                  parentId: O.none,
                  posts: pipe(getFilteredPosts(postFilter, raffles), domain.Raffles.toReadonlyNonEmptyArray)
                })
              ),
              RR.merge(
                RR.singleton<FilterCategory>('podcasts', {
                  id: 'podcasts' as tt.NonEmptyString,
                  icon: O.of('fa-solid fa-headphones'),
                  name: 'Podcasts' as tt.NonEmptyString,
                  slug: 'podcasts' as tt.NonEmptyString,
                  parentId: O.none,
                  posts: pipe(getFilteredPosts(postFilter, podcasts), domain.Podcasts.toReadonlyNonEmptyArray)
                })
              ),
              RR.merge(
                pipe(
                  categories,
                  RR.reject(category => category.slug === 'uncategorized'),
                  RR.map(category => ({
                    ...category,
                    posts: pipe(getFilteredPosts(postFilter, category.posts), domain.Posts.toReadonlyNonEmptyArray)
                  }))
                )
              ),
              setCategories
            )
            pipe(ads, domain.Ads.toReadonlyArray, setAds)
          })
        )
      ),
      O.map(E.match(console.error, constUndefined))
    )
  }, [data, postFilter])

  React.useEffect(() => {
    pipe(
      activeFilter,
      tt.NonEmptyString.decode,
      E.chainW(activeFilter =>
        pipe(
          categories,
          RR.lookup(activeFilter),
          O.map(({ posts }) => posts),
          E.fromOption(constUndefined)
        )
      ),
      E.altW(() =>
        pipe(
          categories,
          RR.map(({ posts }) => posts),
          RR.values,
          RA.flatten,
          RA.reject(domain.CombinedPost.ADT.is.Placeholder),
          RA.sort(domain.CombinedPost.OrdByDate),
          E.of
        )
      ),
      E.map(
        flow(
          RNEA.fromReadonlyArray,
          O.fold(() => RNEA.of(placeholder), identity)
        )
      ),
      E.map(posts =>
        pipe(
          ads,
          RNEA.fromReadonlyArray,
          O.map(Rnd.randomElem),
          O.sequence(IO.Applicative),
          IO.map(
            O.fold(
              () => posts,
              ad =>
                pipe(
                  posts,
                  RA.insertAt<domain.CombinedPost.Type>(1, ad),
                  O.fold(() => RA.concat(posts)(RA.of(ad)), identity)
                )
            )
          )
        )()
      ),
      E.map(setFilteredPosts)
    )
  }, [categories, activeFilter, ads])

  const onSetFilter = (id: string) => pipe(id === activeFilter ? '' : id, setActiveFilter)

  const getPostFetcher = React.useCallback(() => {
    const fetcher = function* () {
      const allChunks = pipe(filteredPosts, RA.chunksOf(5))
      const [firstChunks, lastChunk] = pipe(
        allChunks,
        RA.foldRight(
          () => [RA.zero<ReadonlyArray<domain.CombinedPost.Type>>(), RA.zero<domain.CombinedPost.Type>()],
          (firstChunks, lastChunk) => [firstChunks, lastChunk]
        )
      )
      for (const chunk of firstChunks) {
        yield chunk
      }
      return lastChunk
    }
    return fetcher()
  }, [filteredPosts])

  React.useEffect(() => {
    pipe(
      categories,
      RR.values,
      RA.findFirst(category => category.slug === filter),
      O.map(category => setActiveFilter(category.id))
    )
  }, [categories, filter])

  return (
    <>
      {!hideBar && <CategoriesFilter categories={categories} handleSetFilter={onSetFilter} activeFilter={activeFilter} />}
      {pipe(
        filteredPosts,
        RNEA.fromReadonlyArray,
        O.foldMap(react.monoid)(() => <PostList fetcher={getPostFetcher()} />)
      )}
      {loading && <LoadingSpinner />}
    </>
  )
}
